import { useCallback } from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { getPipelineReport } from '@/api/reports';
import useDashboardRead from './useDashboardRead';
import { pipelineDay } from '@/utils/dashboardReports';

const WEEK_LENGTH = 7;

const usePipeline = (timezone) => {
  const load = useCallback(async (isCurrent) => {
      // First call omits `date` so the backend determines "today" in the
      // shop's timezone; the next 6 days are computed from that response
      // rather than the browser's local date, avoiding timezone drift.
      const todayData = pipelineDay(await getPipelineReport({ timezone }));
      if (!isCurrent()) return;
      const todayDate = parseISO(todayData.date);

      const futureDates = Array.from({ length: WEEK_LENGTH - 1 }, (_, i) =>
        format(addDays(todayDate, i + 1), 'yyyy-MM-dd')
      );

      const futureResponses = await Promise.all(
        futureDates.map((date) =>
          getPipelineReport({ date, timezone })
        )
      );

      return [todayData, ...futureResponses.map((response, index) => {
        const day = pipelineDay(response);
        if (day.date !== futureDates[index]) throw new Error('Pipeline date unavailable');
        return day;
      })];
  }, [timezone]);
  const result = useDashboardRead(timezone, load);
  return { days: result.data, loading: result.status === 'loading',
    error: result.status === 'unavailable', refresh: result.refresh };
};

export default usePipeline;
